import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { 
  BookOpen, Search, Plus, Trash2, Play, ChevronLeft, ChevronRight, Grid, List, 
  Download, Copy, Video, Sparkles, AlertCircle, Info, Sliders, Layout, 
  Code, Megaphone, Terminal, FileText, CheckCircle, BarChart3 
} from "lucide-react";
import axiosInstance from "../../../../axios";
import { Loading } from "../../../Other/Loading";
import { toast } from "react-toastify";

const Webinar = () => {
  const { courses } = useSelector((state) => state?.course || {});
  const { user } = useSelector((state) => state?.users || {});
  const allCourses = courses?.all?.courses || [];

  // Core Template States
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [generatedPrompt, setGeneratedPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // Form Field & Advanced Prompt States
  const [newTemplate, setNewTemplate] = useState({
    templateName: "",
    templateFrom: "Webinar Outline", // Category/Type
    topics: "",
    duration: 60,
    audience: "Beginner",
    tone: "Energetic",
    framework: "AIDA",
    cta: "Join Advanced Course",
    temperature: 0.7
  });

  // Presentation Slider States
  const [slides, setSlides] = useState([]);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [previewTab, setPreviewTab] = useState("deck"); // "deck" or "raw"
  const [simulatedVotes, setSimulatedVotes] = useState([45, 30, 25]); // Live simulated interactive poll votes

  // Filters, Pagination, and Layouts
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [viewMode, setViewMode] = useState("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const [templatesPerPage] = useState(6);

  // Course Filter States
  const [courseSearchTerm, setCourseSearchTerm] = useState("");
  const [coursePage, setCoursePage] = useState(1);
  const [coursesPerPage] = useState(6);

  // Theme support mappings using css variables (Gray and White Minimal style)
  const themeBg = { backgroundColor: "var(--theme-bg, #f8fafc)" };
  const themeCard = { 
    backgroundColor: "var(--theme-card-bg, #ffffff)", 
    color: "var(--theme-text, #1e293b)", 
    borderColor: "var(--theme-border, #e2e8f0)" 
  };
  const themeText = { color: "var(--theme-text, #1e293b)" };
  const themeBorder = { borderColor: "var(--theme-border, #e2e8f0)" };

  // Fetch all saved templates from database
  const fetchTemplates = async () => {
    try {
      const response = await axiosInstance.get("/templates/getAll");
      if (response?.data?.success) {
        // Filter templates belonging to the Webinar categories
        const webinarCats = ["Webinar Outline", "Speaker Notes", "Promotion Email", "Syllabus Slides"];
        const filtered = (response?.data?.data || []).filter(t => 
          webinarCats.includes(t.templateFrom)
        );
        setTemplates(filtered);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  // Filter templates list
  const filteredTemplates = useMemo(() => {
    return templates.filter(template => {
      const matchesSearch = template.templateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.prompt.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = typeFilter === "all" || template.templateFrom === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [templates, searchTerm, typeFilter]);

  // Filter courses list
  const filteredCourses = useMemo(() => {
    return allCourses.filter(course =>
      course.CourseName.toLowerCase().includes(courseSearchTerm.toLowerCase())
    );
  }, [allCourses, courseSearchTerm]);

  // Pagination for templates
  const totalTemplatePages = Math.ceil(filteredTemplates.length / templatesPerPage);
  const currentTemplates = useMemo(() => {
    const startIndex = (currentPage - 1) * templatesPerPage;
    return filteredTemplates.slice(startIndex, startIndex + templatesPerPage);
  }, [filteredTemplates, currentPage, templatesPerPage]);

  // Pagination for courses
  const totalCoursePages = Math.ceil(filteredCourses.length / coursesPerPage);
  const currentCourses = useMemo(() => {
    const startIndex = (coursePage - 1) * coursesPerPage;
    return filteredCourses.slice(startIndex, startIndex + coursesPerPage);
  }, [filteredCourses, coursePage, coursesPerPage]);

  // Quick Pre-Engineered Blueprints
  const handleApplyBlueprint = (blueprintType) => {
    const defaultTopics = selectedCourse ? `Intro to ${selectedCourse.CourseName}, Core Architectures, Live Implementations` : "Core Principles, Advanced Techniques, Interactive Case Studies";
    
    switch (blueprintType) {
      case "masterclass":
        setNewTemplate({
          ...newTemplate,
          templateName: `Masterclass on ${selectedCourse ? selectedCourse.CourseName : "AI Architecture"}`,
          topics: defaultTopics,
          duration: 90,
          audience: "Intermediate",
          tone: "Energetic",
          framework: "AIDA",
          cta: "Join Advanced Course"
        });
        toast.info("Masterclass blueprint applied.");
        break;
      case "technical":
        setNewTemplate({
          ...newTemplate,
          templateName: `Technical Bootcamp: ${selectedCourse ? selectedCourse.CourseName : "Next-Gen Devs"}`,
          topics: "First-Principle Deep Dives, Advanced Live Debugging, Operational Testing",
          duration: 120,
          audience: "Expert",
          tone: "Academic",
          framework: "Syllabus-Led",
          cta: "Apply for Corporate Training"
        });
        toast.info("Technical bootcamp blueprint applied.");
        break;
      case "pitch":
        setNewTemplate({
          ...newTemplate,
          templateName: `Introductory Seminar: Mastering ${selectedCourse ? selectedCourse.CourseName : "Frameworks"}`,
          topics: "Current Market Pain-Points, Real-World Solutions, Fast-Track Career Growth",
          duration: 60,
          audience: "Beginner",
          tone: "Storytelling",
          framework: "PAS",
          cta: "Join Free Study Group"
        });
        toast.info("Conversion pitch blueprint applied.");
        break;
      default:
        break;
    }
  };

  // Compile Slide Deck structure from template details (Minimal Gray and White Palette)
  const compileVisualSlides = (template) => {
    const courseName = selectedCourse ? selectedCourse.CourseName : "AI Advanced Studies";
    const timing1 = Math.round(template.duration * 0.15);
    const timing2 = Math.round(template.duration * 0.45);
    const timing3 = Math.round(template.duration * 0.25);
    const timing4 = Math.round(template.duration * 0.15);

    const generatedSlides = [
      {
        title: "Slide 1: Welcome & Strategic Context",
        icon: <Megaphone className="text-slate-500 dark:text-slate-400" />,
        duration: timing1,
        objective: `Introduce "${template.templateName}" & define core learning milestones.`,
        description: `Set clear ground rules, welcome all participants, and explain why mastering ${template.topics.split(",")[0] || "this topic"} is vital for ${courseName}.`,
        speakerScript: `Hello everyone, welcome! Today we are launching our special session: "${template.templateName}". Over the next ${template.duration} minutes, we will map out an expert strategy. Our main goal is resolving key bottlenecks using a structured ${template.framework} approach. Let's launch a quick poll to see our starting baseline!`,
        widgetType: "poll",
        pollQuestion: `What is your current skill level in ${template.topics.split(",")[0] || "this framework"}?`,
        pollOptions: [
          { text: "🔰 Beginner / Curious Explorer", index: 0 },
          { text: "⚡ Intermediate Developer", index: 1 },
          { text: "🔥 Expert Systems Architect", index: 2 }
        ]
      },
      {
        title: "Slide 2: Theoretical Deep Dive & Concepts",
        icon: <Layout className="text-slate-500 dark:text-slate-400" />,
        duration: timing2,
        objective: `Explore first principles and underlying structural frameworks.`,
        description: `Deconstruct theoretical bounds of "${template.topics}". Break down critical architecture trade-offs, standard pitfalls, and state-of-the-art implementations.`,
        speakerScript: `Now let's unpack the first principles. To really capture the benefits of "${courseName}", we first must master the core pillars. We are using a ${template.tone} delivery tone to keep things engaging, focusing on: ${template.topics}. Feel free to take notes on these key concepts.`,
        widgetType: "concepts",
        concepts: template.topics.split(",").map(t => t.trim())
      },
      {
        title: "Slide 3: Real-World Case Study Demonstration",
        icon: <Code className="text-slate-500 dark:text-slate-400" />,
        duration: timing3,
        objective: `Demonstrate live operational scripting & deployment pipelines.`,
        description: `Walk through a step-by-step modular code simulation that highlights key architectural parameters under a customized ${template.framework} framework.`,
        speakerScript: `Let's switch over to our developer sandbox. We are going to execute a simulated workload demonstrating exactly how these concepts behave in production. Notice the configuration parameters; they match our ${template.audience} level expectations.`,
        widgetType: "code",
        codeSnippet: `// Webinar Demo: ${template.templateName}
import { WebEngine } from "smik-learn-ai-core";

const runMasterclassDemo = async () => {
  console.log("Starting Masterclass Module: ${template.templateName}");
  const engine = new WebEngine({
    difficulty: "${template.audience}",
    framework: "${template.framework}",
    tone: "${template.tone}"
  });

  const result = await engine.initializeSyllabus({
    topics: [${template.topics.split(",").map(t => `"${t.trim()}"`).join(", ")}],
    durationMinutes: ${template.duration}
  });

  console.log("Demo compiled successfully: Status 200 OK");
};
runMasterclassDemo();`
      },
      {
        title: "Slide 4: Q&A Forum & Strategic Invitation",
        icon: <Sparkles className="text-slate-500 dark:text-slate-400" />,
        duration: timing4,
        objective: `Open dialogue for questions and introduce the CTA: ${template.cta}.`,
        description: `Answer technical queries, distribute resources, and share special enrollments statistics to guide students to their next milestone.`,
        speakerScript: `We will open the floor for questions. Feel free to submit your queries in the chat window. To close, for anyone wanting to fast-track their expertise in "${courseName}", please click our strategic link below: "${template.cta}"!`,
        widgetType: "cta",
        ctaTitle: template.cta,
        stats: {
          satisfaction: "99.2% Class Rating",
          attendees: "1,240+ Active Learners",
          status: "Special Offer Available"
        }
      }
    ];

    setSlides(generatedSlides);
    setActiveSlideIndex(0);
  };

  // Generate Prompt via AI Endpoint
  const handleGeneratePrompt = async (e) => {
    e.preventDefault();
    if (!newTemplate.templateName || !newTemplate.topics) {
      toast.error("Please fill out the webinar title and key topics!");
      return;
    }

    setIsLoading(true);
    setGeneratedPrompt("");
    setSlides([]);

    try {
      const response = await axiosInstance.post("/templates/generate", {
        templateName: newTemplate.templateName,
        templateFrom: newTemplate.templateFrom
      });

      const courseName = selectedCourse ? selectedCourse.CourseName : "Advanced Technology Studies";
      let promptText = "";

      if (response?.data?.prompt) {
        promptText = response.data.prompt;
      } else {
        // High quality local compilation in case AI returned empty or fails
        promptText = `Engineered Outline compiled for: ${newTemplate.templateName}`;
      }

      const enrichedPrompt = `
[WEBINAR OUTLINE BLUEPRINT]
==================================================
CONFIGURATION SUMMARY
==================================================
Webinar Title     : ${newTemplate.templateName}
Target Course     : ${courseName}
Duration          : ${newTemplate.duration} Minutes
Format            : ${newTemplate.templateFrom}
Audience          : ${newTemplate.audience}
Tone              : ${newTemplate.tone}
Framework         : ${newTemplate.framework}
Call to Action    : ${newTemplate.cta}
Creativity        : ${newTemplate.temperature}
Topics            : ${newTemplate.topics}

--------------------------------------------------
DELIVERY GUIDELINES:
--------------------------------------------------
- Maintain a ${newTemplate.tone} tone throughout the presentation.
- Apply the ${newTemplate.framework} framework to structure content flow.
- Direct attendees toward the call to action: "${newTemplate.cta}".
- Calibrate technical depth for a ${newTemplate.audience} audience.

--------------------------------------------------
GENERATED CONTENT:
--------------------------------------------------
${promptText}

--------------------------------------------------
SLIDE OUTLINE:
* Slide 1: Welcome & Context (15%)
  - Welcome attendees and establish learning objectives.
  - Open poll on "${newTemplate.topics.split(",")[0] || "core concept"}".

* Slide 2: Core Concepts (45%)
  - Cover the key principles of "${newTemplate.topics}".

* Slide 3: Live Demonstration (25%)
  - Walk through a practical example within ${courseName}.

* Slide 4: Q&A & Next Steps (15%)
  - Address questions and present the call to action: "${newTemplate.cta}".
`;

      setGeneratedPrompt(enrichedPrompt.trim());
      compileVisualSlides(newTemplate);
      toast.success("Webinar outline and slide deck generated.");
    } catch (error) {
      toast.error("Generation failed — using offline fallback.");
      
      const offlinePrompt = `
[WEBINAR OUTLINE — OFFLINE MODE]
Webinar Title  : ${newTemplate.templateName}
Target Course  : ${selectedCourse ? selectedCourse.CourseName : "Advanced Studies"}
Duration       : ${newTemplate.duration} Minutes
Format         : ${newTemplate.templateFrom}
Audience       : ${newTemplate.audience}
Tone           : ${newTemplate.tone}
Framework      : ${newTemplate.framework}
Call to Action : ${newTemplate.cta}
Topics         : ${newTemplate.topics}

--------------------------------------------------
STRUCTURED OUTLINE:
--------------------------------------------------
1. INTRODUCTION & OBJECTIVES (15%)
   - Welcome attendees and outline session goals.
   - Introduce the topic: ${newTemplate.topics.split(",")[0] || "Core Concepts"}.
2. CORE CONCEPTS & THEORY (45%)
   - In-depth coverage of "${newTemplate.topics}".
3. PRACTICAL DEMONSTRATION (25%)
   - Live walkthrough tailored for ${newTemplate.audience} learners.
4. Q&A & NEXT STEPS (15%)
   - Open the floor for questions and present: "${newTemplate.cta}".
`;
      setGeneratedPrompt(offlinePrompt.trim());
      compileVisualSlides(newTemplate);
    } finally {
      setIsLoading(false);
    }
  };

  // Save the generated prompt to the database templates
  const handleSaveToDatabase = async () => {
    if (!generatedPrompt) {
      toast.error("Please generate the prompt template first!");
      return;
    }

    try {
      const response = await axiosInstance.post("/templates/create", {
        templateName: newTemplate.templateName,
        templateFrom: newTemplate.templateFrom,
        prompt: generatedPrompt,
        createdBy: user?.id || null
      });

      if (response?.data?.success) {
        toast.success("Template saved successfully.");
        fetchTemplates();
        setShowForm(false);
        setGeneratedPrompt("");
        setSlides([]);
      }
    } catch (error) {
      toast.error("Failed to save template to the database.");
    }
  };

  // Delete Template from DB
  const handleDeleteTemplate = async (templateId) => {
    try {
      const response = await axiosInstance.delete(`/templates/delete/${templateId}`);
      if (response?.data?.success || response?.status === 200) {
        toast.success("Template deleted successfully.");
        fetchTemplates();
        if (selectedTemplate?.id === templateId) {
          setSelectedTemplate(null);
          setSlides([]);
          setGeneratedPrompt("");
        }
      }
    } catch (error) {
      toast.error("Error deleting template.");
    }
  };

  const handleCopyClipboard = () => {
    if (!generatedPrompt) return;
    navigator.clipboard.writeText(generatedPrompt);
    toast.success("Prompt copied to clipboard!");
  };

  const handleDownloadTxt = () => {
    if (!generatedPrompt) return;
    const blob = new Blob([generatedPrompt], { type: "text/plain;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `${newTemplate.templateName.replace(/\s+/g, "_")}_Webinar_Template.txt`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Outline prompt downloaded as TXT!");
  };

const handleVotePoll = (index) => {
    const updated = [...simulatedVotes];
    updated[index] += 1;
    setSimulatedVotes(updated);
    toast.success("Vote recorded.");
  };

  return (
    <div className="min-h-screen p-6 md:p-8 lg:p-10 transition-colors duration-500" style={themeBg}>
      {/* ── Title Banner ── */}
      <div className="relative overflow-hidden rounded-3xl p-6 md:p-8 mb-8 bg-gradient-to-r from-indigo-950 via-slate-900 to-indigo-900 text-white border border-indigo-900/40 shadow-xl shadow-indigo-950/10 dark:shadow-slate-950/50">
        <div className="absolute -right-10 -top-10 w-48 h-48 bg-indigo-500/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -left-10 -bottom-10 w-48 h-48 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />
        
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div className="flex items-center gap-4">
            <div className="p-4 bg-white/10 backdrop-blur-md text-white rounded-2xl border border-white/20 shadow-md">
              <Video size={28} className="text-indigo-200 animate-pulse" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-0.5 text-[10px] font-black uppercase bg-indigo-500/35 border border-indigo-400/40 text-indigo-200 rounded-full tracking-wider">
                  AI Prompt Studio
                </span>
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-ping" />
              </div>
              <h1 className="text-2xl md:text-3xl font-black tracking-tight mt-1 text-white">
                AI Webinar Prompt Architect
              </h1>
              <p className="text-indigo-200/75 text-xs md:text-sm font-medium mt-1">
                Build structured webinar outlines, interactive slide decks, speaker scripts, and promotional campaigns.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Main Grid Workspace ── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        
        {/* ── Left Column Sidebar: Course Selection ── */}
        <div className="xl:col-span-1">
          <div 
            className="rounded-3xl border p-6 shadow-md shadow-slate-100/30 dark:shadow-none sticky top-6 transition-all duration-300 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md"
            style={{ 
              borderColor: "var(--theme-border, #e2e8f0)",
              color: "var(--theme-text, #1e293b)" 
            }}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-black tracking-tight flex items-center gap-2" style={themeText}>
                <BookOpen className="text-indigo-500 shrink-0" size={20} />
                Target Courses
              </h2>
              <span className="bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 px-3 py-1 rounded-full text-[10px] font-black border border-indigo-100 dark:border-indigo-900/60 uppercase tracking-wider">
                {filteredCourses.length} Available
              </span>
            </div>

            {/* Course Search */}
            <div className="relative mb-5">
              {/* <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" /> */}
              <input
                type="text"
                placeholder="Search course titles..."
                value={courseSearchTerm}
                onChange={(e) => {
                  setCourseSearchTerm(e.target.value);
                  setCoursePage(1);
                }}
                className="w-full pl-11 pr-4 py-3 border rounded-2xl text-xs font-semibold focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500/80 transition-all bg-slate-50/50 dark:focus:bg-slate-950"
                style={{ 
                  borderColor: "var(--theme-border, #e2e8f0)",
                  color: "var(--theme-text, #1e293b)"
                }}
              />
            </div>

            {/* Course List Card Stack */}
            <div className="space-y-3 max-h-[490px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-800">
              {currentCourses.length > 0 ? (
                currentCourses.map((course) => {
                  const isSelected = selectedCourse?.ID === course.ID;
                  return (
                    <div
                      key={course.ID}
                      className={`group p-5 rounded-2xl border transition-all duration-300 cursor-pointer relative overflow-hidden flex flex-col justify-between hover:scale-[1.01] active:scale-[0.99] ${
                        isSelected
                          ? "bg-gradient-to-br from-indigo-600 via-indigo-700 to-indigo-800 border-indigo-500 text-white shadow-md shadow-indigo-600/10 dark:shadow-none"
                          : "bg-slate-50/40 hover:bg-white dark:bg-slate-900/50 dark:hover:bg-slate-900/80 border-slate-200/60 dark:border-slate-800/60 hover:border-indigo-300 dark:hover:border-indigo-900/60 hover:shadow-xs"
                      }`}
                      style={!isSelected ? { borderColor: "var(--theme-border)" } : {}}
                      onClick={() => {
                        setSelectedCourse(course);
                        // Auto-initialize default title based on selected course name
                        setNewTemplate(prev => ({
                          ...prev,
                          templateName: `Webinar: Deep Dive on ${course.CourseName}`
                        }));
                      }}
                    >
                      {isSelected && (
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-indigo-400 to-purple-500" />
                      )}
                      
                      <div className="flex gap-3 items-start">
                        {isSelected ? (
                          <CheckCircle className="w-3.5 h-3.5 text-indigo-200 shrink-0 mt-0.5 animate-in zoom-in" />
                        ) : (
                          <BookOpen className="w-3.5 h-3.5 text-slate-400 group-hover:text-indigo-500 transition-colors shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className={`font-extrabold text-xs leading-snug mb-2 transition-colors duration-200 ${
                            isSelected ? "text-white" : "text-slate-800 dark:text-slate-200 group-hover:text-indigo-650 dark:group-hover:text-indigo-400"
                          }`}>
                            {course.CourseName}
                          </h3>
                          <div className={`flex justify-between items-center mt-2 pt-1 border-t border-dashed ${
                            isSelected ? "border-indigo-500/35" : "border-slate-100 dark:border-slate-800/40"
                          }`}>
                            <span className={`text-[9px] font-extrabold uppercase tracking-wider ${isSelected ? "text-indigo-200/80" : "text-slate-400"}`}>
                              ID: #{course.ID}
                            </span>
                            <span className={`text-[9px] font-black uppercase tracking-wider ${isSelected ? "text-white" : "text-slate-400 group-hover:text-indigo-500"}`}>
                              {isSelected ? "✦ Active Target" : "Select Target"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-slate-400 font-semibold text-xs border border-dashed rounded-2xl border-slate-200 dark:border-slate-800">
                  No matching courses found.
                </div>
              )}
            </div>

            {/* Course Pagination */}
            {totalCoursePages > 1 && (
              <div className="flex justify-center items-center gap-3 mt-5 pt-4 border-t" style={themeBorder}>
                <button
                  onClick={() => setCoursePage(prev => Math.max(prev - 1, 1))}
                  disabled={coursePage === 1}
                  className="p-2 rounded-xl border disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors border-slate-200 dark:border-slate-800"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs font-black" style={themeText}>
                  {coursePage} / {totalCoursePages}
                </span>
                <button
                  onClick={() => setCoursePage(prev => Math.min(prev + 1, totalCoursePages))}
                  disabled={coursePage === totalCoursePages}
                  className="p-2 rounded-xl border disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors border-slate-200 dark:border-slate-800"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── Right Column: Workspace Panel ── */}
        <div className="xl:col-span-2 flex flex-col gap-6">
          {selectedCourse ? (
            <>
              {/* Header Configuration Panel */}
              <div 
                className="rounded-3xl border p-6 shadow-md bg-white dark:bg-slate-900" 
                style={{ borderColor: "var(--theme-border, #e2e8f0)" }}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-black tracking-tight flex items-center gap-2" style={themeText}>
                      <Sliders className="text-indigo-500 shrink-0" size={20} />
                      Architect Workspace
                    </h2>
                    <p className="text-xs text-slate-400 font-semibold mt-1">
                      Configure AI parameters and design presentation outlines.
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowForm(!showForm);
                      setGeneratedPrompt("");
                      setSlides([]);
                    }}
                    className="bg-slate-900 hover:bg-black text-white dark:bg-white dark:hover:bg-slate-50 dark:text-slate-900 px-4 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider shadow-sm transition-all active:scale-95 flex items-center gap-2 border border-slate-950 dark:border-white self-start sm:self-center cursor-pointer"
                  >
                    {showForm ? <List className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                    {showForm ? "View Catalog" : "Create New Blueprint"}
                  </button>
                </div>

                {!showForm && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                      <div className="relative sm:col-span-2">
                        <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                        <input
                          type="text"
                          placeholder="Search saved blueprints..."
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                          }}
                          className="w-full pl-10 pr-4 py-2.5 border rounded-xl text-xs font-semibold focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 focus:outline-none transition-all bg-slate-50/50"
                          style={{
                            borderColor: "var(--theme-border, #e2e8f0)",
                            color: "var(--theme-text, #1e293b)"
                          }}
                        />
                      </div>

                      <select
                        value={typeFilter}
                        onChange={(e) => {
                          setTypeFilter(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="border rounded-xl px-3 py-2.5 text-xs font-extrabold focus:outline-none focus:ring-2 focus:ring-indigo-500/20 bg-white dark:bg-slate-900"
                        style={{
                          borderColor: "var(--theme-border, #e2e8f0)",
                          color: "var(--theme-text, #1e293b)"
                        }}
                      >
                        <option value="all">All Formats</option>
                        <option value="Webinar Outline">Webinar Outline</option>
                        <option value="Speaker Notes">Speaker Notes</option>
                        <option value="Syllabus Slides">Syllabus Slides</option>
                        <option value="Promotion Email">Promotion Email</option>
                      </select>
                    </div>

                    <div className="flex justify-between items-center text-[10px] font-black text-slate-400 uppercase tracking-widest mt-5 pt-3 border-t" style={themeBorder}>
                      <span>{filteredTemplates.length} Blueprints available</span>
                      <div className="flex bg-slate-100 dark:bg-slate-950 rounded-xl p-1 shrink-0 border border-slate-200/50 dark:border-slate-800/80">
                        <button
                          onClick={() => setViewMode("grid")}
                          className={`p-1.5 rounded-lg transition-colors cursor-pointer ${viewMode === "grid" ? "bg-white dark:bg-slate-800 shadow-xs text-slate-800 dark:text-slate-100" : "text-slate-400 hover:text-slate-700"}`}
                        >
                          <Grid className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setViewMode("list")}
                          className={`p-1.5 rounded-lg transition-colors cursor-pointer ${viewMode === "list" ? "bg-white dark:bg-slate-800 shadow-xs text-slate-800 dark:text-slate-100" : "text-slate-400 hover:text-slate-700"}`}
                        >
                          <List className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Form Option: Engineer New Prompt Panel */}
              {showForm ? (
                <div 
                  className="rounded-3xl border p-6 md:p-8 shadow-md bg-white dark:bg-slate-900" 
                  style={{ borderColor: "var(--theme-border, #e2e8f0)" }}
                >
                  
                  {/* Presets Card Section */}
                  <div className="mb-6 p-4 md:p-5 rounded-2xl bg-slate-50/50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div>
                      <h4 className="text-xs font-black flex items-center gap-1.5 uppercase tracking-wider text-slate-700 dark:text-slate-300">
                        <Sparkles size={14} className="text-indigo-500 dark:text-indigo-400 animate-pulse" />
                        Quick-Start Presets
                      </h4>
                      <p className="text-[10px] text-slate-400 font-semibold mt-1">
                        Select a design pattern to instantly configure the workspace.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => handleApplyBlueprint("masterclass")}
                        className="px-3 py-1.5 bg-white hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-lg text-[10px] font-bold uppercase tracking-wider border border-slate-200 dark:border-slate-800 shadow-xs transition-all active:scale-95 cursor-pointer"
                      >
                        🎓 Masterclass
                      </button>
                      <button
                        type="button"
                        onClick={() => handleApplyBlueprint("technical")}
                        className="px-3 py-1.5 bg-white hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-lg text-[10px] font-bold uppercase tracking-wider border border-slate-200 dark:border-slate-800 shadow-xs transition-all active:scale-95 cursor-pointer"
                      >
                        💻 Technical bootcamp
                      </button>
                      <button
                        type="button"
                        onClick={() => handleApplyBlueprint("pitch")}
                        className="px-3 py-1.5 bg-white hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-lg text-[10px] font-bold uppercase tracking-wider border border-slate-200 dark:border-slate-800 shadow-xs transition-all active:scale-95 cursor-pointer"
                      >
                        🔥 Conversion pitch
                      </button>
                    </div>
                  </div>

                  <form onSubmit={handleGeneratePrompt} className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Webinar Title</label>
                        <input
                          type="text"
                          placeholder="e.g. Masterclass: Generative AI Best Practices"
                          value={newTemplate.templateName}
                          onChange={(e) => setNewTemplate({ ...newTemplate, templateName: e.target.value })}
                          className="w-full border px-4 py-3 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all bg-slate-50/50 focus:bg-white dark:focus:bg-slate-950"
                          style={{
                            borderColor: "var(--theme-border, #e2e8f0)",
                            color: "var(--theme-text, #1e293b)"
                          }}
                          required
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Key Focus Topics</label>
                        <input
                          type="text"
                          placeholder="e.g. prompt engineering, model tuning, workflow automation"
                          value={newTemplate.topics}
                          onChange={(e) => setNewTemplate({ ...newTemplate, topics: e.target.value })}
                          className="w-full border px-4 py-3 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all bg-slate-50/50 focus:bg-white dark:focus:bg-slate-950"
                          style={{
                            borderColor: "var(--theme-border, #e2e8f0)",
                            color: "var(--theme-text, #1e293b)"
                          }}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Format Category</label>
                        <select
                          value={newTemplate.templateFrom}
                          onChange={(e) => setNewTemplate({ ...newTemplate, templateFrom: e.target.value })}
                          className="w-full border px-4 py-3 rounded-xl text-xs font-bold focus:outline-none focus:border-indigo-400 bg-slate-50/50 dark:bg-slate-950"
                          style={{
                            borderColor: "var(--theme-border, #e2e8f0)",
                            color: "var(--theme-text, #1e293b)"
                          }}
                        >
                          <option value="Webinar Outline">Webinar Outline</option>
                          <option value="Speaker Notes">Speaker Notes</option>
                          <option value="Syllabus Slides">Syllabus Slides</option>
                          <option value="Promotion Email">Promotion Email</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Duration</label>
                        <select
                          value={newTemplate.duration}
                          onChange={(e) => setNewTemplate({ ...newTemplate, duration: Number(e.target.value) })}
                          className="w-full border px-4 py-3 rounded-xl text-xs font-bold focus:outline-none focus:border-indigo-400 bg-slate-50/50 dark:bg-slate-950"
                          style={{
                            borderColor: "var(--theme-border, #e2e8f0)",
                            color: "var(--theme-text, #1e293b)"
                          }}
                        >
                          <option value={30}>30 Minutes</option>
                          <option value={60}>60 Minutes</option>
                          <option value={90}>90 Minutes</option>
                          <option value={120}>120 Minutes</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Audience Target</label>
                        <select
                          value={newTemplate.audience}
                          onChange={(e) => setNewTemplate({ ...newTemplate, audience: e.target.value })}
                          className="w-full border px-4 py-3 rounded-xl text-xs font-bold focus:outline-none focus:border-indigo-400 bg-slate-50/50 dark:bg-slate-950"
                          style={{
                            borderColor: "var(--theme-border, #e2e8f0)",
                            color: "var(--theme-text, #1e293b)"
                          }}
                        >
                          <option value="Beginner">🔰 Beginner Explorer</option>
                          <option value="Intermediate">⚡ Intermediate Developer</option>
                          <option value="Expert">🔥 Expert Architect</option>
                        </select>
                      </div>
                    </div>

                    {/* Advanced Parameters Expansion */}
                    <div className="border-t pt-5 mt-5" style={themeBorder}>
                      <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-700 dark:text-slate-300 mb-4 flex items-center gap-1.5">
                        <Sliders size={14} className="text-indigo-500" />
                        Advanced Engine Parameters
                      </h4>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                        <div>
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Delivery Tone</label>
                          <select
                            value={newTemplate.tone}
                            onChange={(e) => setNewTemplate({ ...newTemplate, tone: e.target.value })}
                            className="w-full border px-4 py-3 rounded-xl text-xs font-bold focus:outline-none focus:border-indigo-400 bg-slate-50/50 dark:bg-slate-950"
                            style={{
                              borderColor: "var(--theme-border, #e2e8f0)",
                              color: "var(--theme-text, #1e293b)"
                            }}
                          >
                            <option value="Energetic">Energetic & Inspiring</option>
                            <option value="Professional">Professional & Corporate</option>
                            <option value="Academic">Academic & Analytical</option>
                            <option value="Storytelling">Narrative & Storytelling</option>
                          </select>
                        </div>

                        <div>
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Copy Framework</label>
                          <select
                            value={newTemplate.framework}
                            onChange={(e) => setNewTemplate({ ...newTemplate, framework: e.target.value })}
                            className="w-full border px-4 py-3 rounded-xl text-xs font-bold focus:outline-none focus:border-indigo-400 bg-slate-50/50 dark:bg-slate-950"
                            style={{
                              borderColor: "var(--theme-border, #e2e8f0)",
                              color: "var(--theme-text, #1e293b)"
                            }}
                          >
                            <option value="AIDA">AIDA (Attention, Interest, Desire, Action)</option>
                            <option value="PAS">PAS (Problem, Agitate, Solution)</option>
                            <option value="Hook-Story-Offer">Hook, Story, Offer Matrix</option>
                            <option value="Syllabus-Led">Syllabus Core Sequence</option>
                          </select>
                        </div>

                        <div>
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Primary Conversion CTA</label>
                          <select
                            value={newTemplate.cta}
                            onChange={(e) => setNewTemplate({ ...newTemplate, cta: e.target.value })}
                            className="w-full border px-4 py-3 rounded-xl text-xs font-bold focus:outline-none focus:border-indigo-400 bg-slate-50/50 dark:bg-slate-950"
                            style={{
                              borderColor: "var(--theme-border, #e2e8f0)",
                              color: "var(--theme-text, #1e293b)"
                            }}
                          >
                            <option value="Join Advanced Course">Join Complete Course Plan</option>
                            <option value="Apply for Corporate Training">Request Corporate B2B Plan</option>
                            <option value="Join Free Study Group">Join Free Student Community</option>
                            <option value="Schedule 1-on-1 Consultation">Schedule Instructor Mentoring</option>
                          </select>
                        </div>
                      </div>

                      {/* Temperature Range Slider */}
                      <div className="mt-5">
                        <div className="flex justify-between items-center mb-1.5">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                            Creativity Intensity (Temperature)
                          </label>
                          <span className="text-[10px] font-extrabold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 px-2 py-0.5 rounded-md border border-indigo-150 dark:border-indigo-900/60 shadow-xs">
                            {newTemplate.temperature}
                          </span>
                        </div>
                        <input 
                          type="range" 
                          min="0.2" 
                          max="1.0" 
                          step="0.1" 
                          value={newTemplate.temperature}
                          onChange={(e) => setNewTemplate({ ...newTemplate, temperature: parseFloat(e.target.value) })}
                          className="w-full h-1.5 bg-slate-100 dark:bg-slate-950 rounded-lg appearance-none cursor-pointer accent-indigo-600 border border-slate-200 dark:border-slate-800"
                        />
                        <div className="flex justify-between text-[8px] text-slate-400 font-bold uppercase mt-1 tracking-wider">
                          <span>Deterministic</span>
                          <span>Creative (Default)</span>
                          <span>Experimental</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3 mt-6 pt-4 border-t animate-in fade-in" style={themeBorder}>
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white font-black px-5 py-3 rounded-xl text-xs uppercase tracking-wider shadow-sm transition-all active:scale-95 disabled:opacity-50 flex items-center gap-2 border border-indigo-700 cursor-pointer"
                      >
                        {isLoading ? "Generating..." : "Generate Outline"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowForm(false);
                          setGeneratedPrompt("");
                          setSlides([]);
                        }}
                        className="bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-750 text-slate-700 dark:text-slate-300 font-black px-5 py-3 rounded-xl text-xs uppercase tracking-wider transition-all active:scale-95 cursor-pointer"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>

                  {/* LOADING GRAPHICS */}
                  {isLoading && (
                    <div className="mt-8 flex flex-col items-center justify-center p-12 bg-slate-50/50 dark:bg-slate-950/40 border-2 border-dashed rounded-3xl border-slate-200 dark:border-slate-800">
                      <Loading />
                      <p className="text-slate-500 dark:text-slate-400 text-xs font-bold mt-4 animate-pulse uppercase tracking-wider">
                        Generating Outline via AI...
                      </p>
                    </div>
                  )}

                  {/* GENERATED OUTCOME DISPLAY */}
                  {generatedPrompt && !isLoading && (
                    <div className="mt-8 border-t pt-8 animate-in fade-in slide-in-from-bottom-5" style={themeBorder}>
                      
                      {/* Tabs Controller */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
                        <div className="flex bg-slate-100 dark:bg-slate-950 p-1 rounded-xl border border-slate-200/80 dark:border-slate-800" style={themeBorder}>
                          <button
                            type="button"
                            onClick={() => setPreviewTab("deck")}
                            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${
                              previewTab === "deck"
                                ? "bg-white dark:bg-slate-900 shadow-xs text-slate-800 dark:text-white border border-slate-200/50 dark:border-slate-800"
                                : "text-slate-500 hover:text-slate-800"
                            }`}
                          >
                            <Layout size={13} className="text-indigo-500" />
                            Visual Deck Player
                          </button>
                          <button
                            type="button"
                            onClick={() => setPreviewTab("raw")}
                            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${
                              previewTab === "raw"
                                ? "bg-white dark:bg-slate-900 shadow-xs text-slate-800 dark:text-white border border-slate-200/50 dark:border-slate-800"
                                : "text-slate-500 hover:text-slate-800"
                            }`}
                          >
                            <FileText size={13} className="text-indigo-500" />
                            Raw Prompt Template
                          </button>
                        </div>

                        {/* Export Buttons */}
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={handleCopyClipboard}
                            className="p-2.5 text-slate-500 hover:text-slate-950 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-950 rounded-xl transition-all border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 cursor-pointer"
                            title="Copy to Clipboard"
                          >
                            <Copy size={15} />
                          </button>
                          <button
                            type="button"
                            onClick={handleDownloadTxt}
                            className="p-2.5 text-slate-500 hover:text-slate-950 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-950 rounded-xl transition-all border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 cursor-pointer"
                            title="Download TXT file"
                          >
                            <Download size={15} />
                          </button>
                        </div>
                      </div>

                      {/* TAB: VISUAL DECK MODE */}
                      {previewTab === "deck" && slides.length > 0 && (
                        <div className="rounded-3xl border overflow-hidden shadow-lg border-slate-200 dark:border-slate-800 bg-slate-950 text-slate-100 flex flex-col">
                          
                          {/* Active Slide Screen */}
                          <div className="p-8 md:p-10 min-h-[380px] flex flex-col justify-between relative bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-slate-950">
                            
                            {/* Visual segmented duration bar indicator */}
                            <div className="flex h-1.5 bg-slate-900 overflow-hidden rounded-full mb-6">
                              <div className={`h-full transition-all duration-300 ${activeSlideIndex >= 0 ? "bg-indigo-500" : "bg-slate-800"}`} style={{ width: '15%' }} title="Slide 1: Welcome (15%)" />
                              <div className={`h-full border-l border-slate-950 transition-all duration-300 ${activeSlideIndex >= 1 ? "bg-purple-500" : "bg-slate-800"}`} style={{ width: '45%' }} title="Slide 2: Concepts (45%)" />
                              <div className={`h-full border-l border-slate-950 transition-all duration-300 ${activeSlideIndex >= 2 ? "bg-emerald-500" : "bg-slate-800"}`} style={{ width: '25%' }} title="Slide 3: Code Demo (25%)" />
                              <div className={`h-full border-l border-slate-950 transition-all duration-300 ${activeSlideIndex >= 3 ? "bg-amber-500" : "bg-slate-800"}`} style={{ width: '15%' }} title="Slide 4: Q&A & CTA (15%)" />
                            </div>

                            {/* Header Info */}
                            <div className="flex justify-between items-center mb-6 relative z-10">
                              <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
                                <span className="p-1.5 bg-slate-900 rounded-lg text-indigo-400 border border-slate-800">
                                  {slides[activeSlideIndex].icon}
                                </span>
                                {slides[activeSlideIndex].title}
                              </div>
                              <span className="bg-slate-900/90 text-indigo-300 px-3 py-1.5 rounded-full text-[10px] font-black border border-slate-800 flex items-center gap-1">
                                ⏱️ {slides[activeSlideIndex].duration} MINS
                              </span>
                            </div>

                            {/* Body content */}
                            <div className="flex-1 relative z-10">
                              <h3 className="text-lg md:text-xl font-black mb-3 text-white leading-snug tracking-tight">
                                {slides[activeSlideIndex].objective}
                              </h3>
                              <p className="text-slate-400 text-xs md:text-sm font-medium max-w-2xl leading-relaxed">
                                {slides[activeSlideIndex].description}
                              </p>

                              {/* Interactive Widgets container */}
                              <div className="mt-8 p-6 rounded-2xl bg-slate-950 border border-slate-900 shadow-inner">
                                
                                {/* Case 1: Live Poll simulation */}
                                {slides[activeSlideIndex].widgetType === "poll" && (
                                  <div>
                                    <h4 className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-4 flex items-center gap-1.5">
                                      <BarChart3 size={12} className="text-indigo-400" />
                                      Interactive Poll: {slides[activeSlideIndex].pollQuestion}
                                    </h4>
                                    <div className="space-y-2.5">
                                      {slides[activeSlideIndex].pollOptions.map((opt, idx) => {
                                        const total = simulatedVotes.reduce((a, b) => a + b, 0);
                                        const pct = Math.round((simulatedVotes[opt.index] / total) * 100) || 0;
                                        return (
                                          <div 
                                            key={idx} 
                                            onClick={() => handleVotePoll(opt.index)}
                                            className="group cursor-pointer p-3.5 rounded-xl border border-slate-900 bg-slate-950/60 hover:bg-slate-900 hover:border-slate-800 transition-all flex justify-between items-center relative overflow-hidden"
                                          >
                                            {/* Dynamic color fill bar background */}
                                            <div 
                                              className="absolute left-0 top-0 bottom-0 bg-indigo-500/10 transition-all duration-700 pointer-events-none" 
                                              style={{ width: `${pct}%` }}
                                            ></div>
                                            <span className="relative z-10 text-[11px] font-bold text-slate-300 group-hover:text-white transition-colors">
                                              {opt.text}
                                            </span>
                                            <div className="relative z-10 flex items-center gap-2.5">
                                              <span className="text-xs font-black text-indigo-400">{pct}%</span>
                                              <span className="text-[9px] text-slate-500 font-bold uppercase">({simulatedVotes[opt.index]} votes)</span>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}

                                {/* Case 2: Topics checklist */}
                                {slides[activeSlideIndex].widgetType === "concepts" && (
                                  <div>
                                    <h4 className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-4 flex items-center gap-1.5">
                                      <CheckCircle size={12} className="text-emerald-400" />
                                      Syllabus Core Focus Areas
                                    </h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                      {slides[activeSlideIndex].concepts.map((concept, idx) => (
                                        <div key={idx} className="p-3 bg-slate-950 border border-slate-900 rounded-xl flex items-center gap-3 shadow-xs">
                                          <span className="w-5 h-5 bg-indigo-950/50 text-indigo-400 rounded-lg flex items-center justify-center text-[10px] font-black shrink-0 border border-indigo-900/50">
                                            {idx + 1}
                                          </span>
                                          <span className="text-xs font-extrabold text-slate-300">{concept}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Case 3: Code sandbox display */}
                                {slides[activeSlideIndex].widgetType === "code" && (
                                  <div>
                                    <h4 className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-1.5">
                                      <Terminal size={12} className="text-emerald-400 animate-pulse" />
                                      Sandbox Code Compiler Emulator
                                    </h4>
                                    <div className="font-mono text-xs overflow-x-auto leading-relaxed max-h-[160px] overflow-y-auto text-emerald-400 bg-slate-905 bg-slate-950 p-4 rounded-xl shadow-inner border border-slate-900 scrollbar-thin scrollbar-thumb-slate-800">
                                      <pre className="whitespace-pre-wrap">{slides[activeSlideIndex].codeSnippet}</pre>
                                    </div>
                                  </div>
                                )}

                                {/* Case 4: CTA strategic pitch card */}
                                {slides[activeSlideIndex].widgetType === "cta" && (
                                  <div className="text-center p-1">
                                    <h4 className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-4">
                                      Conversion Proposition
                                    </h4>
                                    <div className="max-w-md mx-auto p-4.5 rounded-2xl bg-slate-950 border border-slate-900 flex flex-col items-center">
                                      <span className="text-white bg-indigo-600 px-4 py-1.5 rounded-xl text-[10px] font-black border border-indigo-500/50 mb-3 flex items-center gap-1.5 shadow-sm uppercase tracking-wider">
                                        🚀 {slides[activeSlideIndex].ctaTitle}
                                      </span>
                                      <p className="text-slate-400 text-xs font-semibold mb-4 leading-normal">
                                        Ask students to enroll and claim this limited-time offer.
                                      </p>
                                      <div className="flex gap-5 justify-center w-full border-t border-slate-900 pt-3 text-[9px] font-extrabold uppercase tracking-widest text-slate-500">
                                        <div>⭐ {slides[activeSlideIndex].stats.satisfaction}</div>
                                        <div className="h-3 w-px bg-slate-900" />
                                        <div>👥 {slides[activeSlideIndex].stats.attendees}</div>
                                      </div>
                                    </div>
                                  </div>
                                )}

                              </div>
                            </div>

                            {/* Speaker notes drawer footer section */}
                            <div className="mt-8 pt-5 border-t border-slate-900 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 relative z-10">
                              <div className="flex items-center gap-2.5 bg-slate-950 text-slate-400 px-4 py-3 rounded-2xl text-[11px] font-semibold max-w-xl border border-slate-900">
                                <Info size={15} className="shrink-0 text-indigo-400" />
                                <span className="italic leading-normal">
                                  <strong>Presenter Script:</strong> {slides[activeSlideIndex].speakerScript.slice(0, 140)}...
                                </span>
                              </div>

                              {/* Slider Navigation */}
                              <div className="flex gap-2 self-end shrink-0">
                                <button
                                  type="button"
                                  onClick={() => setActiveSlideIndex(prev => Math.max(prev - 1, 0))}
                                  disabled={activeSlideIndex === 0}
                                  className="px-3.5 py-2 bg-slate-900 hover:bg-slate-800 text-slate-300 rounded-xl text-xs font-extrabold disabled:opacity-30 disabled:cursor-not-allowed transition-all border border-slate-800 cursor-pointer"
                                >
                                  Previous
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setActiveSlideIndex(prev => Math.min(prev + 1, slides.length - 1))}
                                  disabled={activeSlideIndex === slides.length - 1}
                                  className="px-3.5 py-2 bg-white hover:bg-slate-50 text-slate-900 rounded-xl text-xs font-extrabold disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-md cursor-pointer"
                                >
                                  Next Slide
                                </button>
                              </div>
                            </div>

                          </div>

                          {/* Pagination indicators footer overlay */}
                          <div className="bg-slate-950 p-4 border-t border-slate-900 flex justify-center gap-2">
                            {slides.map((_, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() => setActiveSlideIndex(i)}
                                className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                                  activeSlideIndex === i ? "bg-indigo-500 w-6" : "bg-slate-800 hover:bg-slate-700"
                                }`}
                              ></button>
                            ))}
                          </div>

                        </div>
                      )}

                      {/* TAB: RAW TEXT BLUEPRINT */}
                      {previewTab === "raw" && (
                        <div className="bg-slate-950 p-6 rounded-3xl border border-slate-900 text-slate-300 font-mono text-xs overflow-x-auto leading-relaxed shadow-inner max-h-[380px] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-800">
                          <pre className="whitespace-pre-wrap">{generatedPrompt}</pre>
                        </div>
                      )}

                      {/* Save Blueprint button */}
                      <div className="flex gap-3 mt-6">
                        <button
                          type="button"
                          onClick={handleSaveToDatabase}
                          className="bg-indigo-600 hover:bg-indigo-750 text-white font-black px-5 py-3 rounded-xl text-xs uppercase tracking-wider shadow-sm transition-all active:scale-95 flex items-center gap-2 border border-indigo-700 cursor-pointer"
                        >
                          <Plus size={14} />
                          Save Prompt Blueprint to Library
                        </button>
                      </div>

                    </div>
                  )}

                </div>
              ) : (
                
                /* Saved Templates Catalog View */
                <div 
                  className="rounded-3xl border p-6 shadow-md bg-white dark:bg-slate-900" 
                  style={{ borderColor: "var(--theme-border, #e2e8f0)" }}
                >
                  {currentTemplates.length > 0 ? (
                    <>
                      <div className={
                        viewMode === "grid"
                          ? "grid grid-cols-1 md:grid-cols-2 gap-5 animate-in fade-in"
                          : "space-y-4 animate-in fade-in"
                      }>
                        {currentTemplates.map((template) => (
                          <div
                            key={template.id}
                            className={`border rounded-2xl p-5 flex flex-col justify-between transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-md ${
                              selectedTemplate?.id === template.id
                                ? "border-indigo-600 dark:border-indigo-500 bg-indigo-50/20 dark:bg-indigo-950/20 shadow-xs"
                                : "bg-white dark:bg-slate-950"
                            } ${viewMode === "list" ? "flex-row items-center gap-4" : ""}`}
                            style={{ borderColor: "var(--theme-border)" }}
                          >
                            <div className={viewMode === "list" ? "flex-1 min-w-0" : "min-w-0"}>
                              <div className="flex items-center gap-2.5 mb-2">
                                <span className="p-2 rounded-xl bg-slate-50 dark:bg-slate-900 text-indigo-500 border border-slate-100 dark:border-slate-800 shadow-xs">
                                  <Video size={15} />
                                </span>
                                <h3 className="font-extrabold text-xs tracking-tight truncate max-w-[200px]" style={themeText} title={template.templateName}>
                                  {template.templateName}
                                </h3>
                              </div>

                              <div className={`${viewMode === "list" ? "flex items-center gap-4 text-[10px] font-semibold" : "space-y-1.5 text-[10px] font-semibold"} text-slate-500 mt-2`}>
                                <div className="flex items-center gap-2">
                                  <span className="bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 px-2.5 py-0.5 rounded-full border border-indigo-100 dark:border-indigo-900/60 text-[9px] font-black uppercase tracking-wider">
                                    {template.templateFrom}
                                  </span>
                                  {template.createdOn && (
                                    <span className="text-[9px] text-slate-400 font-extrabold uppercase">
                                      🗓️ {new Date(template.createdOn).toLocaleDateString()}
                                    </span>
                                  )}
                                </div>
                                <div className="truncate max-w-[260px] italic text-[10px] text-slate-400 font-medium" title={template.prompt}>
                                  Snippet: {template.prompt.slice(0, 100)}...
                                </div>
                              </div>
                            </div>

                            <div className={`${viewMode === "list" ? "flex shrink-0 ml-auto" : "flex mt-4 pt-3 border-t border-slate-50 dark:border-slate-900/60"} gap-2`}>
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedTemplate(template);
                                  setGeneratedPrompt(template.prompt);
                                  
                                  // Map saved prompt parameters back into template settings if present or guess
                                  setNewTemplate({
                                    templateName: template.templateName,
                                    templateFrom: template.templateFrom,
                                    topics: "Core Modules, Syllabus deep-dive, Interactive Practice",
                                    duration: 60,
                                    audience: "Intermediate",
                                    tone: "Energetic",
                                    framework: "AIDA",
                                    cta: "Join Advanced Course",
                                    temperature: 0.7
                                  });
                                  
                                  // Re-compile visual deck slides based on standard reconstruction
                                  compileVisualSlides({
                                    templateName: template.templateName,
                                    templateFrom: template.templateFrom,
                                    topics: "Core Modules, Syllabus deep-dive, Interactive Practice",
                                    duration: 60,
                                    audience: "Intermediate",
                                    tone: "Energetic",
                                    framework: "AIDA",
                                    cta: "Join Advanced Course"
                                  });

                                  setShowForm(true);
                                  setPreviewTab("deck");
                                }}
                                className="bg-slate-900 hover:bg-black text-white dark:bg-white dark:hover:bg-slate-50 dark:text-slate-900 px-3 py-2 rounded-lg font-bold text-[10px] uppercase tracking-wider shadow-xs transition-all active:scale-95 flex items-center gap-1.5 cursor-pointer"
                              >
                                <Play className="w-2.5 h-2.5 fill-current" />
                                Present Outline
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteTemplate(template.id)}
                                className="bg-white hover:bg-rose-50 text-rose-500 border border-rose-200 p-2 rounded-lg transition-all active:scale-95 shrink-0 cursor-pointer dark:bg-slate-950 dark:border-slate-800"
                                title="Delete Blueprint"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Templates Pagination */}
                      {totalTemplatePages > 1 && (
                        <div className="flex justify-center items-center gap-2 mt-6 pt-5 border-t" style={themeBorder}>
                          <button
                            type="button"
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="p-2 rounded-xl border disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-50 transition-colors border-slate-200 dark:border-slate-800"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </button>

                          <div className="flex gap-1">
                            {Array.from({ length: totalTemplatePages }, (_, i) => (
                              <button
                                key={i + 1}
                                type="button"
                                onClick={() => setCurrentPage(i + 1)}
                                className={`w-9 h-9 rounded-xl font-bold text-xs transition-all cursor-pointer ${
                                  currentPage === i + 1
                                    ? "bg-slate-900 text-white shadow-xs"
                                    : "border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900"
                                }`}
                              >
                                {i + 1}
                              </button>
                            ))}
                          </div>

                          <button
                            type="button"
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalTemplatePages))}
                            disabled={currentPage === totalTemplatePages}
                            className="p-2 rounded-xl border disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-50 transition-colors border-slate-200 dark:border-slate-800"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    /* Empty catalog screen */
                    <div className="text-center py-12 flex flex-col items-center justify-center">
                      <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 border border-dashed border-slate-300 dark:border-slate-800 bg-slate-50 dark:bg-slate-955/60" style={themeBorder}>
                        <AlertCircle className="text-slate-400" size={24} />
                      </div>
                      <h3 className="text-sm font-black tracking-tight mb-1" style={themeText}>No Saved Blueprints</h3>
                      <p className="text-slate-500 dark:text-slate-400 text-xs font-semibold max-w-xs leading-normal mb-5">
                        {searchTerm || typeFilter !== "all"
                          ? "Try adjusting your filters or clearing the search query."
                          : "No saved templates for this course yet. Create a new blueprint to begin."}
                      </p>
                      {!searchTerm && typeFilter === "all" && (
                        <button
                          type="button"
                          onClick={() => setShowForm(true)}
                          className="bg-slate-900 hover:bg-black text-white dark:bg-white dark:hover:bg-slate-50 dark:text-slate-900 px-4 py-2 rounded-xl font-bold text-xs uppercase tracking-wider shadow-sm transition-all active:scale-95 flex items-center gap-1.5 border border-slate-950 dark:border-white cursor-pointer"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Create First Blueprint
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            
            /* Initial Select Course screen */
            <div 
              className="rounded-3xl border p-16 text-center shadow-md bg-white dark:bg-slate-900 min-h-[500px] flex flex-col items-center justify-center relative overflow-hidden" 
              style={{ borderColor: "var(--theme-border, #e2e8f0)" }}
            >
              <div className="absolute -right-20 -top-20 w-60 h-60 bg-indigo-500/5 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute -left-20 -bottom-20 w-60 h-60 bg-purple-500/5 rounded-full blur-3xl pointer-events-none" />
              
              <div className="bg-slate-50 dark:bg-slate-950/60 w-20 h-20 rounded-2xl flex items-center justify-center mb-6 border border-slate-200 dark:border-slate-800 shadow-inner relative z-10">
                <Video size={36} className="text-indigo-500/80 dark:text-indigo-400 animate-pulse animate-duration-1000 shrink-0" />
              </div>
              <h3 className="text-xl font-black tracking-tight mb-2 relative z-10" style={themeText}>
                Webinar Prompt Architect
              </h3>
              <p className="text-slate-500 dark:text-slate-400 text-xs md:text-sm font-medium max-w-sm leading-relaxed mb-6 mx-auto relative z-10">
                Select a target course from the sidebar to activate the engineering workspace and start building presentations.
              </p>
              <div className="p-3.5 bg-slate-50 dark:bg-slate-950/80 border border-slate-200/60 dark:border-slate-800/80 text-slate-600 dark:text-slate-300 rounded-2xl text-[11px] font-semibold flex items-center gap-2.5 max-w-xs mx-auto shadow-sm relative z-10" style={themeBorder}>
                <Info size={14} className="text-indigo-500 shrink-0" />
                <span>Compatible with all system courses.</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Webinar;